"use client"

import type React from "react"

import { observer } from "mobx-react-lite"
import { type FormEvent, useState } from "react"
import { useAppContext } from "../../../../shared/functions/Context"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { type ILeadership, defaultLeadership } from "../../../../shared/models/three-sixty-feedback-models/Leadership"

const NewLeadershipModal = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson
  const [leadership, setLeadership] = useState<ILeadership>(defaultLeadership)

  const [loading, setLoading] = useState(false)

  const [statements, setStatements] = useState<{ id: string; statement: string; rating: number }[]>([
    { id: "1", statement: "", rating: 0 },
    { id: "2", statement: "", rating: 0 },
    { id: "3", statement: "", rating: 0 },
  ])

  const handleStatementChange = (index: number, statement: string) => {
    const updatedStatements = [...statements]
    updatedStatements[index].statement = statement
    setStatements(updatedStatements)
  }

  const addStatement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const newStatement = {
      id: new Date().toISOString(),
      statement: "",
      rating: 0, // Include default rating
    }
    setStatements([...statements, newStatement])
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    // Ensure updatedLeadership includes rating
    const updatedLeadership = {
      ...leadership,
      statements: statements.map((statement) => ({
        ...statement,
        rating: statement.rating ?? 0, // Ensure rating is always included
      })),
    }

    if (!me) return
    await create(updatedLeadership)
    console.log("Leadership, ", updatedLeadership)
    setLoading(false)
    onCancel()
  }

  const create = async (leadership: ILeadership) => {
    try {
      await api.leadership.create(leadership)
    } catch (error) {
      console.error("Error creating leadership:", error)
    }
  }

  const onCancel = () => {
    setLeadership({ ...defaultLeadership })
    setStatements([
      { id: "1", statement: "", rating: 0 },
      { id: "2", statement: "", rating: 0 },
      { id: "3", statement: "", rating: 0 },
    ])

    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_LEADERSHIP_MODAL)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 7h-4V4c0-1.103-.897-2-2-2h-4c-1.103 0-2 .897-2 2v3H4c-1.103 0-2 .897-2 2v11c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V9c0-1.103-.897-2-2-2zM10 4h4v3h-4V4zm10 16H4V9h16v11z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">New Leadership Criteria</h3>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828z"></path>
              <path d="M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8z"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="criteria-name" className="text-sm font-medium text-gray-700">
              Criteria Name
            </label>
            <input
              id="criteria-name"
              type="text"
              placeholder="Enter criteria name"
              value={leadership.criteriaName}
              onChange={(e) =>
                setLeadership({
                  ...leadership,
                  criteriaName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Statements</label>
            <div className="space-y-2">
              {statements.map((statement, index) => (
                <div key={statement.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={`Enter Statement ${index + 1}`}
                    value={statement.statement}
                    onChange={(e) => handleStatementChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updatedStatements = statements.filter((_, i) => i !== index)
                      setStatements(updatedStatements)
                    }}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M5 20a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8h2V6h-4V4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2H3v2h2zM9 4h6v2H9zM8 8h9v12H7V8z"></path>
                      <path d="M9 10h2v8H9zm4 0h2v8h-2z"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addStatement}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Statement
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!leadership.criteriaName.trim() || statements.some((s) => !s.statement.trim()) || loading}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default NewLeadershipModal
